import * as React from "react";
import { Helmet } from "react-helmet";

import AcornsHeader from "../AcornsHeader/AcornsHeader";
import AppFooter from "../../../components/AppLayout/AppFooter";
import styled from "../../../styled-components";
import { media } from "../../../breakpoints";

const Container = styled.div`
  margin: 0 auto;
  max-width: 800px;
  padding: 0 0 90px;

  ${media.TABLET`
    padding: 0 20px 50px;
  `};
`;

const Title = styled.h1`
  color: ${props => props.theme.colors.white};
  font-family: ${props => props.theme.fonts.sans};
  font-size: 36px;
  font-weight: 400;
  margin: 25px 0;
  text-align: center;
  text-shadow: 0 0 4px rgba(0, 0, 0, 0.12);

  ${media.TABLET`
    font-size: 28px;
    margin: 0 0 40px 0;
  `};
`;

const MainContent = styled.section`
  background: ${props => props.theme.colors.black};
  color: ${props => props.theme.colors.white};
  width: 100%;
`;

const Answer = styled.div`
  font-size: 14px;
  letter-spacing: 0.19px;
  line-height: 1.7;
  margin-bottom: 0;
  margin-top: 10px;
`;

const Copyright = Answer.extend`
  margin-top: 60px;
`;

const Question = styled.h2`
  font-size: 17px;
  font-weight: 600;
  letter-spacing: 0.23px;
  line-height: 1.4;
  margin-bottom: 0;
  margin-top: 40px;

  &:first-of-type {
    margin-top: 0;
  }

  &:target {
    &,
    & + ${Answer} {
      border-left: 5px solid ${props => props.theme.colors.black};
      padding-left: 10px;
      margin-left: -15px;
    }
    & + ${Answer} {
      margin-top: 0;
      padding-top: 10px;
    }
  }
`;

const AnswerLink = styled.a`
  color: ${props => props.theme.colors.white};
  text-decoration: underline;
`;

const AcornsFaq: React.SFC<{}> = () => (
  <>
    <Helmet>
      <title>Say - Acorns Faq</title>
    </Helmet>

    <MainContent>
      <AcornsHeader />
      <Title>Frequently Asked Questions</Title>
      <Container>
        <Question id="say">Who is Say?</Question>
        <Answer>
          <p>
            We’re a financial services company using technology to empower
            investors, because we believe that investors have certain
            fundamental rights:
          </p>
          <ol>
            <li>
              All shareholders should be heard, not just the biggest ones.
            </li>
            <li>
              Barriers that prevent communication between companies and their
              owners should be removed.
            </li>
            <li>
              Investing in accessible investment products like ETFs shouldn’t
              mean forfeiting the right to participate as an owner.
            </li>
          </ol>
        </Answer>

        <Question id="launch">When does the Say app launch?</Question>
        <Answer>
          <p>
            The Say app launches in early 2019. Get early access{" "}
            <AnswerLink href="https://saytechnologies.com/acorns">
              here
            </AnswerLink>
            .
          </p>
        </Answer>

        <Question id="investor-rights">
          What rights do I have as an investor?
        </Question>
        <Answer>
          <p>
            The exact nature of your rights depends on what you own (i.e. do you
            own shares of a fund, or a public company?) but can include the
            right to submit proposals, the right to vote on company decisions,
            the right to elect members of the board and the right to receive
            information about your investments. Acorns currently only enables
            its customers to invest in funds.
          </p>
        </Answer>

        <Question id="fund-public-difference">
          What is the difference between a fund and a public company?
        </Question>
        <Answer>
          <p>
            A fund is an investment vehicle that pools money from fund
            shareholders who own shares in the fund. The fund then purchases and
            holds shares of public companies, other funds or other assets. As
            those assets increase or decrease in value, so too do shares of the
            fund.
          </p>
          <p>
            A public company is a single company whose shares can be bought and
            sold on a public exchange - this is why they are called “public”
            companies. Because funds and public companies are different kinds of
            organizations, their investors have different kinds of rights.
          </p>
        </Answer>

        <Question id="what-happens-shareholder">
          What happens at a shareholder meeting?
        </Question>
        <Answer>
          <p>
            Funds and public companies can hold meetings where they ask their
            shareholders to make decisions. A key right as a shareholder is to
            participate in these meetings, including by attending in person.
          </p>
        </Answer>

        <Question id="when">When do I vote?</Question>
        <Answer>
          <p>
            You will get plenty of notice before a vote. Voting happens annually
            for public companies, but sometimes “special” meetings for
            shareholders to vote are held outside of the normal schedule. Funds,
            unlike public companies, hold shareholder meetings only when certain
            changes are being proposed.
          </p>
        </Answer>

        <Question id="how">How do I vote?</Question>
        <Answer>
          <p>
            You can usually vote online, by phone, or even by physical mail. If
            you are a shareholder, you’ll receive information before any vote to
            help you make your decision.
          </p>
        </Answer>

        <Question id="voting-in-person">Can I vote in person?</Question>
        <Answer>
          <p>
            Yes! Shareholders can usually attend meetings in person, though some
            companies and funds may have their own rules about who can show up.
          </p>
        </Answer>

        <Question id="my-info-rights">
          What information do I have a right to?
        </Question>
        <Answer>
          <p>
            Shareholders have the right to receive and review reports to help
            them keep track of how the company or fund is doing.
          </p>
        </Answer>

        <Question id="information-reports">
          What kind of information do these reports contain?
        </Question>
        <Answer>
          <p>
            They discuss topics such as the company or fund’s performance and
            provide details about important events, sometimes even before they
            occur. They can contain things like descriptions of executive pay,
            financial statements showing how the company or fund performed over
            the last year and results of votes at company or fund meetings.
          </p>
        </Answer>

        <Question id="where-to-find-reports">
          Where can I find these reports?
        </Question>
        <Answer>
          <p>
            Some reports are so important that funds and companies are required
            to send them directly to shareholders, while others are posted
            online. Checking the document section of a fund’s website, or
            checking a company’s website for their “investor relations” page is
            usually a good start.
          </p>
        </Answer>

        <Question id="acorns">
          Why is Say sending me an email on behalf of Acorns?
        </Question>
        <Answer>
          <p>
            Say works with Acorns to distribute shareholder communications to
            Acorns customers. We coordinate with Acorns and the funds that
            Acorns customers invest in in order to make sure that required
            shareholder materials are provided to every Acorns customer. We are
            not affiliated with Vanguard or Blackrock or any other public
            company or fund, however.
          </p>
        </Answer>

        <Question id="information-collected">
          What user information do you collect?
        </Question>
        <Answer>
          <p>
            Acorns provides Say with information about its customers so that we
            can provide them with shareholder materials. We use the information
            we receive to obtain shareholder materials from the funds that
            Acorns customers invest in so that we can distribute them.
          </p>
          <p>
            We value the privacy of all individuals who visit our website and
            use our services. We collect information to tailor your personal
            experience, efficiently facilitate your interactions with our
            platform, analyze user trends, contact you for administrative
            purposes, comply with legal requirements, and prevent fraud.
          </p>
          <p>
            For a further explanation please review our{" "}
            <AnswerLink href="https://saytechnologies.com/privacy">
              Privacy Policy
            </AnswerLink>{" "}
            and{" "}
            <AnswerLink href="https://saytechnologies.com/terms">
              Terms of Service
            </AnswerLink>
            .
          </p>
        </Answer>

        <Question id="what-else">What else should I know?</Question>
        <Answer>
          <p>
            Say is not a broker-dealer, investment adviser, or law firm, and
            does not provide any investment, tax, or legal advice. We are not
            affiliated with Vanguard or Blackrock.
          </p>
        </Answer>

        <Copyright>
          <div>© Say Communications LLC</div>
          <div>
            <AnswerLink href="https://saytechnologies.com/terms">
              Terms
            </AnswerLink>{" "}
            &{" "}
            <AnswerLink href="https://saytechnologies.com/privacy">
              Privacy
            </AnswerLink>
          </div>
          <div>
            <AnswerLink href="https://jobs.lever.co/say">
              We're Hiring
            </AnswerLink>
          </div>

          <div>
            <AnswerLink href="mailto:support@saytechnologies.com">
              support@saytechnologies.com
            </AnswerLink>
          </div>
        </Copyright>
      </Container>
      <AppFooter hideVoterInfo={true} />
    </MainContent>
  </>
);

export default AcornsFaq;



// WEBPACK FOOTER //
// ./src/pages/Acorns/AcornsFaq/AcornsFaq.tsx